import React, { useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  IconButton,
  Switch,
  Typography,
  Divider,
  Tooltip,
} from "@mui/material";
import { MoreVert as MoreIcon } from "@mui/icons-material";
import CustomButton from "../../../../components/common/button/CustomButton";

interface LeadTypeRule {
  leadType: string;
  assignTo: string;
  status: boolean;
}

const USERS = ["User A", "User B", "User C", "User D"];
const LEAD_TYPE_OPTIONS = ["Buyer", "Seller", "Landlord", "Tenant", "Investor"];

const LeadTypeAllocation = () => {
  const [rules, setRules] = useState<LeadTypeRule[]>([
    { leadType: "Buyer",    assignTo: "User A", status: true },
    { leadType: "Seller",   assignTo: "User B", status: true },
    { leadType: "Landlord", assignTo: "User C", status: false },
  ]);

  const handleChange = (index: number, field: string, value: any) => {
    setRules((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleDelete = (index: number) => {
    setRules((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAdd = () => {
    setRules((prev) => [...prev, { leadType: "Buyer", assignTo: "User A", status: true }]);
  };

  return (
    <Paper sx={{ padding: "16px", borderRadius: "8px" }}>
      {/* Header */}
      <Box sx={{ marginBottom: "12px" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#333", fontSize: "14px", marginBottom: "4px" }}>
          Lead Type-Based Allocation
        </Typography>
        <Typography variant="caption" sx={{ color: "#666", fontSize: "12px" }}>
          Assign leads based on lead type.
        </Typography>
      </Box>

      {/* Table */}
      <TableContainer sx={{ border: "1px solid #e0e0e0", borderRadius: "6px", marginBottom: "16px" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              {["Lead Type", "Assign To", "Status", "Action"].map((h, i) => (
                <TableCell
                  key={h}
                  align={i === 3 ? "center" : "left"}
                  sx={{ fontWeight: 600, color: "#333", fontSize: "12px", padding: "6px 8px" }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.map((rule, i) => (
              <TableRow key={i} sx={{ "&:hover": { backgroundColor: "#fafafa" } }}>
                {/* Lead Type */}
                <TableCell sx={{ padding: "6px 8px" }}>
                  <Select
                    value={rule.leadType}
                    onChange={(e) => handleChange(i, "leadType", e.target.value)}
                    size="small"
                    sx={{ minWidth: "130px", fontSize: "12px", height: "28px" }}
                  >
                    {LEAD_TYPE_OPTIONS.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </TableCell>

                {/* Assign To */}
                <TableCell sx={{ padding: "6px 8px" }}>
                  <Select
                    value={rule.assignTo}
                    onChange={(e) => handleChange(i, "assignTo", e.target.value)}
                    size="small"
                    sx={{ minWidth: "110px", fontSize: "12px", height: "28px" }}
                  >
                    {USERS.map((u) => (
                      <MenuItem key={u} value={u}>{u}</MenuItem>
                    ))}
                  </Select>
                </TableCell>

                {/* Status */}
                <TableCell sx={{ padding: "6px 8px" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <Switch
                      checked={rule.status}
                      onChange={(e) => handleChange(i, "status", e.target.checked)}
                      size="small"
                    />
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "11px", fontWeight: 500, color: rule.status ? "#02906f" : "#999" }}
                    >
                      {rule.status ? "Active" : "Disabled"}
                    </Typography>
                  </Box>
                </TableCell>

                {/* Action */}
                <TableCell align="center" sx={{ padding: "6px 8px" }}>
                  <Tooltip title="Delete rule">
                    <IconButton size="small" onClick={() => handleDelete(i)} sx={{ padding: "2px" }}>
                      <MoreIcon sx={{ fontSize: "16px" }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <CustomButton title="+ Add Rule" onClick={handleAdd} />

      {/* Hint */}
      <Typography variant="caption" sx={{ display: "block", color: "#999", fontSize: "11px", marginTop: "10px", marginBottom: "10px" }}>
        Lead type rules are checked after Area rules. If no match, fallback to percentage allocation.
      </Typography>

      <Divider sx={{ marginBottom: "12px" }} />

      {/* Footer */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Box sx={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#02906f" }} />
          <Typography variant="caption" sx={{ color: "#02906f", fontWeight: 500, fontSize: "13px" }}>
            Rules Valid - All set for now!
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: "10px" }}>
          <CustomButton title="Reset Draft" variant="outlined" onClick={() => {}} />
          <CustomButton title="Save" onClick={() => {}} />
          <CustomButton title="Save & Activate" onClick={() => {}} />
        </Box>
      </Box>
    </Paper>
  );
};

export default LeadTypeAllocation;